// API Service for PHP Backend Integration
import environment from "../config/environment";
import BaseApiService from "./BaseApiService";

class ApiService extends BaseApiService {
  constructor() {
    super(); // Initialize BaseApiService with default PHP API URL and timeout
  }

  // Login method that integrates with LoginEduokCrm.php
  async login(userId, password) {
    try {
      // Login is handled by LoginEduokCrm.php under TARGET_DIR_CRM
      const systemCode = environment.SYSTEM_CODE;
      const applicationId = "";

      const params = new URLSearchParams({
        user_id: userId,
        password: password,
        system_code: systemCode,
        application_id: applicationId,
      });

      const loginUrl = `${environment.SERVER_NAME}${environment.TARGET_DIR_CRM}LoginEduokCrm.php?${params.toString()}`;

      // Always log the full login URL
      console.log("[ApiService] Login API Full URL:", loginUrl);
      
      if (environment.DEBUG_MODE) {
        // Helpful in development to verify the exact URL being called
        // Note: password is present in query for this legacy endpoint; avoid logging credentials in production
        console.log("[ApiService] Login URL (DEBUG):", loginUrl);
      }

      // Call directly to allow different base path than PHP_API_URL
      const response = await this.makeRequest(loginUrl);

      if (response.api_status === "OK") {
        // LoginEduokCrm.php returns admin rows in `data` (not `staff`)
        const staff = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.staff)
            ? response.staff
            : [];
        return {
          success: true,
          data: {
            staff,
            raw: response,
          },
          message: "Login successful",
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Login failed",
          message: response.api_remark || "Login failed",
        };
      }
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Get Admin Dashboard Data
  async getCrmAdminDashboard() {
    try {
      const dashboardUrl = `${environment.SERVER_NAME}${environment.TARGET_DIR_CRM}getCrmAdminDashboard.php`;
      
      console.log("[ApiService] Admin Dashboard API Full URL:", dashboardUrl);
      
      const response = await this.makeRequest(dashboardUrl);

      return this.handleResponse(response, "Dashboard data retrieved successfully", "Failed to fetch dashboard data");
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Method to get user profile data
  async getUserProfile(userId) {
    try {
      const response = await this.makeRequest(
        `GetUserProfile.php?user_id=${userId}`
      );

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get user profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to update user profile
  async updateUserProfile(userId, profileData) {
    try {
      const response = await this.makeRequest("UpdateUserProfile.php", {
        method: "POST",
        body: JSON.stringify({
          user_id: userId,
          ...profileData,
        }),
      });

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to update user profile",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get school data
  async getSchoolData() {
    try {
      const response = await this.makeRequest("GetSchoolData.php");

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get school data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get students data
  async getStudents(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetStudents.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get students data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get teachers data
  async getTeachers(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetTeachers.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get teachers data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get classes data
  async getClasses(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetClasses.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get classes data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get attendance data
  async getAttendance(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetAttendance.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get attendance data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get fees data
  async getFees(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetFees.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get fees data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get grades data
  async getGrades(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetGrades.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get grades data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Method to get reports data
  async getReports(filters = {}) {
    try {
      const params = new URLSearchParams(filters);
      const response = await this.makeRequest(`GetReports.php?${params}`);

      if (response.api_status === "OK") {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get reports data",
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Get Financial Year List
  async getFinancialYearList() {
    try {
      const endpoint = "getFinancialYearList.php";
      const fullUrl = `${this.baseURL}/${endpoint}`;

      console.log("[ApiService] getFinancialYearList API Call:");
      console.log("  Full URL:", fullUrl);

      const response = await this.makeRequest(endpoint);
      console.log("[ApiService] getFinancialYearList Response:", response);

      // Try to normalize different possible response shapes
      const years =
        response.data ||
        response.financial_years ||
        response.years ||
        response.list ||
        [];

      if (response.api_status === "OK") {
        return {
          success: true,
          data: years,
          message: response.api_remark || "Financial year list retrieved successfully",
        };
      } else {
        return {
          success: false,
          error: response.api_remark || "Failed to get financial year list",
          message: response.api_remark || "Failed to get financial year list",
        };
      }
    } catch (error) {
      console.error("[ApiService] getFinancialYearList Error:", error);
      return {
        success: false,
        error: error.message,
        message: "Network error or server unavailable",
      };
    }
  }

  // Get My Schools - Filtered by financial year and month
  async getMySchools(financialYearId, selectedMonth = null, limit = 100, offset = 0, order = 'ASC', status = 'all') {
    try {
      const params = this.createSearchParams({
        financial_year_id: financialYearId,
        selected_month: selectedMonth,
        limit: limit,
        offset: offset,
        order: order,
        status: status && status !== 'all' ? status : 'all',
      });

      const queryString = params.toString();
      // Use new optimized PHP endpoint with same parameters
      const endpoint = `getMySchoolsNew.php?${queryString}`;
      const fullUrl = `${this.baseURL}/${endpoint}`;
      
      // Log full URL with all parameters
      console.log("[ApiService] getMySchools API Call:");
      console.log("  Full URL:", fullUrl);
      console.log("  Parameters:", {
        financial_year_id: financialYearId,
        selected_month: selectedMonth,
        limit: limit,
        offset: offset,
        order: order,
        status: status || 'all',
      });
      console.log("  Query String:", queryString);

      // Created By Cursor — longer timeout; list query can be heavy on beta
      const response = await this.makeRequest(endpoint, { timeout: 45000 });
      console.log("[ApiService] getMySchools Response:", response);

      // Created By Cursor — support both data[] and data.rows[] (server may nest pagination)
      if (response.api_status === "OK") {
        const raw = response.data;
        const rows = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.rows)
            ? raw.rows
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
        const total =
          typeof response.total !== "undefined"
            ? Number(response.total)
            : typeof raw?.total !== "undefined"
              ? Number(raw.total)
              : rows.length > 0 && typeof rows[0].total !== "undefined"
                ? Number(rows[0].total)
                : rows.length;
        return {
          success: true,
          data: rows,
          total,
          limit:
            response.limit != null
              ? Number(response.limit)
              : raw?.limit != null
                ? Number(raw.limit)
                : limit,
          offset:
            response.offset != null
              ? Number(response.offset)
              : raw?.offset != null
                ? Number(raw.offset)
                : offset,
          message: "Schools retrieved successfully",
        };
      }

      return this.handleResponse(response, 'Schools retrieved successfully', 'Failed to retrieve schools');
    } catch (error) {
      console.error("[ApiService] getMySchools Error:", error);
      return this.handleError(error);
    }
  }


  // Filter/list schools (leads/clients) - wraps filterMySchool.php
  async filterSchools({ limit = 100, offset = 0, order = 'DESC', isStudentFilter = 'FALSE', s_operator = '', s_value = '' } = {}) {
    try {
      const params = this.createSearchParams({ limit, offset, order, isStudentFilter, s_operator, s_value });
      const response = await this.makeRequest(`filterMySchool.php?${params.toString()}`);
      return this.handleResponse(response, 'Schools retrieved successfully', 'Failed to retrieve schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Search schools by keyword - wraps searchSchool.php (option = JSON field array)
  // Created By Cursor
  async searchSchools(keyword, option = ['school_name', 'school_code', 'admin_name', 'admin_phone']) {
    try {
      const optionParam = Array.isArray(option) ? JSON.stringify(option) : option;
      const params = this.createSearchParams({ keyword, option: optionParam });
      const response = await this.makeRequest(`searchSchool.php?${params.toString()}`);
      return this.handleResponse(response, 'Search completed successfully', 'No schools found');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Autocomplete keywords from tm_search_index — wraps getSearchIndex.php
  // Created By Cursor
  async getSearchIndex() {
    try {
      const response = await this.makeRequest('getSearchIndex.php');
      return this.handleResponse(response, 'Search index loaded', 'No search index found');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Searchable field options — wraps getSearchOptions.php
  // Created By Cursor
  async getSearchOptions() {
    try {
      const response = await this.makeRequest('getSearchOptions.php');
      return this.handleResponse(response, 'Search options loaded', 'No search options found');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Create a new school (lead/client) - wraps createSchool.php
  async createSchool({ school_name, school_address, country, state, admin_name, admin_phone }) {
    try {
      const params = this.createSearchParams({ school_name, school_address, country, state, admin_name, admin_phone });
      const response = await this.makeRequest(`createSchool.php?${params.toString()}`);
      return this.handleResponse(response, 'School created successfully', 'Failed to create school');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Update basic school info - wraps updateSchool.php
  async updateSchool({ school_code, school_name, school_address, admin_name, admin_phone, status }) {
    try {
      const params = this.createSearchParams({ school_code, school_name, school_address, admin_name, admin_phone, status });
      const response = await this.makeRequest(`updateSchool.php?${params.toString()}`);
      return this.handleResponse(response, 'School updated successfully', 'Failed to update school');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Delete a school - wraps deleteSchool.php
  async deleteSchool(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(`deleteSchool.php?${params.toString()}`);
      return this.handleResponse(response, 'School deleted successfully', 'Failed to delete school');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Orders / Billing ---

  //Created By Cursor
  async getProductRateList() {
    try {
      const response = await this.makeRequest('getProductRateList.php');
      return this.handleResponse(response, 'Products retrieved successfully', 'Failed to retrieve products');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getOrderList({ limit = 100, offset = 0, order = 'DESC', status_filter = '' } = {}) {
    try {
      const params = this.createSearchParams({ limit, offset, order, status_filter });
      const response = await this.makeRequest(`getOrderList.php?${params.toString()}`);
      return this.handleResponse(response, 'Orders retrieved successfully', 'Failed to retrieve orders');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getOrderDetail(orderId) {
    try {
      const params = this.createSearchParams({ order_id: orderId });
      const response = await this.makeRequest(`getOrderDetail.php?${params.toString()}`);
      return this.handleResponse(response, 'Order retrieved successfully', 'Failed to retrieve order');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getSchoolDetail(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(`getCrmSchoolDetail.php?${params.toString()}`);
      return this.handleResponse(response, 'School detail retrieved', 'Failed to retrieve school detail');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — light school detail for Add New Bill (accounts + subscription rates)
  async getSchoolDetailForBill(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(`getCrmSchoolDetailForBill.php?${params.toString()}`);
      return this.handleResponse(response, 'School detail retrieved', 'Failed to retrieve school detail');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — reset demo start to today + days + remark
  async extendSchoolDemo({ school_code, product_code, extra_days, remark = '' }) {
    try {
      const params = this.createSearchParams({ school_code, product_code, extra_days, remark });
      const response = await this.makeRequest(`extendSchoolDemo.php?${params.toString()}`);
      return this.handleResponse(response, 'Demo extended successfully', 'Failed to extend demo');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — extend / restart grace window (subscription)
  async extendSchoolGrace({ school_code, product_code = '', extra_days, remark = '', created_by = '' }) {
    try {
      const params = this.createSearchParams({
        school_code,
        product_code,
        extra_days,
        remark,
        created_by,
      });
      const response = await this.makeRequest(`extendSchoolGrace.php?${params.toString()}`);
      return this.handleResponse(response, 'Grace extended successfully', 'Failed to extend grace');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — cancel school subscription (status → Cancelled)
  async cancelSchoolSubscription({
    school_code = '',
    product_code = '',
    subscription_id = '',
    remark = '',
    created_by = '',
  } = {}) {
    try {
      const params = this.createSearchParams({
        school_code,
        product_code,
        subscription_id,
        remark,
        created_by,
      });
      const response = await this.makeRequest(`cancelSchoolSubscription.php?${params.toString()}`);
      return this.handleResponse(
        response,
        'Subscription cancelled',
        'Failed to cancel subscription'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — school staff for App Access (optional login_type filter)
  async getSchoolStaff(schoolCode, loginType = 'ALL') {
    try {
      const params = this.createSearchParams({
        school_code: schoolCode,
        login_type: loginType || 'ALL',
      });
      const response = await this.makeRequest(`getSchoolStaff.php?${params.toString()}`);
      if (response?.api_status === 'OK') {
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.staff)
            ? response.staff
            : [];
        return { success: true, data: list, message: 'Staff loaded' };
      }
      return {
        success: false,
        error: response?.api_remark || 'No staff found',
        data: [],
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — billed list with Android/WebERP KPIs + state counts + FY filter
  async getBilledSchools({
    limit = 2000,
    offset = 0,
    financial_year_id = '',
    state = '',
  } = {}) {
    try {
      const params = this.createSearchParams({
        limit,
        offset,
        financial_year_id,
        state,
      });
      const response = await this.makeRequest(`getBilledSchools.php?${params.toString()}`);
      if (response?.api_status === 'OK') {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === 'number' ? response.total : rows.length,
          total_unfiltered:
            typeof response.total_unfiltered === 'number'
              ? response.total_unfiltered
              : typeof response.total === 'number'
                ? response.total
                : rows.length,
          kpis: response.kpis || { android: 0, weberp: 0, total: 0 },
          states: Array.isArray(response.states) ? response.states : [],
          message: 'Billed schools retrieved',
        };
      }
      return this.handleResponse(response, 'Billed schools retrieved', 'Failed to retrieve billed schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getNotBilledSchools({ limit = 100, offset = 0 } = {}) {
    try {
      const params = this.createSearchParams({ limit, offset });
      const response = await this.makeRequest(`getNotBilledSchools.php?${params.toString()}`);
      return this.handleResponse(response, 'Not billed schools retrieved', 'Failed to retrieve not billed schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getExpiredSchools({ limit = 100, offset = 0 } = {}) {
    try {
      const params = this.createSearchParams({ limit, offset });
      const response = await this.makeRequest(`getExpiredSchools.php?${params.toString()}`);
      return this.handleResponse(response, 'Expired schools retrieved', 'Failed to retrieve expired schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — demo extension requests from school Expired gate
  async getDemoExtensionRequests({ limit = 500, offset = 0, request_status = 'ALL' } = {}) {
    try {
      const params = this.createSearchParams({ limit, offset, request_status });
      const response = await this.makeRequest(
        `getDemoExtensionRequests.php?${params.toString()}`
      );
      return this.handleResponse(
        response,
        'Demo extension requests retrieved',
        'Failed to retrieve demo extension requests'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async deleteDemoExtensionRequest(request_id) {
    try {
      const params = this.createSearchParams({ request_id });
      const response = await this.makeRequest(
        `deleteDemoExtensionRequest.php?${params.toString()}`
      );
      return this.handleResponse(
        response,
        'Demo extension request deleted',
        'Failed to delete demo extension request'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async updateDemoExtensionRequestStatus(request_id, request_status) {
    try {
      const params = this.createSearchParams({ request_id, request_status });
      const response = await this.makeRequest(
        `updateDemoExtensionRequestStatus.php?${params.toString()}`
      );
      return this.handleResponse(
        response,
        'Demo extension status updated',
        'Failed to update demo extension status'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — days window OR calendar year / year_month (YYYY-MM)
  // Accepts number (legacy days) or { days, year, year_month }
  async getUpcomingRenewals(daysOrOpts = 30) {
    try {
      let days = 30;
      let year = null;
      let year_month = null;
      if (typeof daysOrOpts === 'object' && daysOrOpts !== null) {
        days = daysOrOpts.days != null ? daysOrOpts.days : 30;
        year = daysOrOpts.year != null ? daysOrOpts.year : null;
        year_month = daysOrOpts.year_month || null;
      } else {
        days = daysOrOpts;
      }
      const paramsObj = {};
      if (year_month) {
        paramsObj.year_month = year_month;
      } else if (year) {
        paramsObj.year = year;
      } else {
        paramsObj.days = days;
      }
      const params = this.createSearchParams(paramsObj);
      const response = await this.makeRequest(`getUpcomingRenewals.php?${params.toString()}`);
      if (response?.api_status === 'OK') {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === 'number' ? response.total : rows.length,
          month_counts: response.month_counts || null,
          month_renewed_counts: response.month_renewed_counts || null,
          renewed_total: typeof response.renewed_total === 'number' ? response.renewed_total : 0,
          pending_total: typeof response.pending_total === 'number' ? response.pending_total : 0,
          due_total: typeof response.due_total === 'number' ? response.due_total : rows.length,
          conversion_rate: typeof response.conversion_rate === 'number' ? response.conversion_rate : 0,
          mode: response.mode || null,
          message: 'Renewals retrieved',
        };
      }
      return this.handleResponse(response, 'Renewals retrieved', 'Failed to retrieve renewals');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getSchoolsInDemo({ demo_days = 'ALL', limit = 100, offset = 0, order = 'DESC' } = {}) {
    try {
      const params = this.createSearchParams({ demo_days, limit, offset, order });
      const response = await this.makeRequest(`getSchoolInDemo.php?${params.toString()}`);
      return this.handleResponse(response, 'Demo schools retrieved', 'Failed to retrieve demo schools');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getOrderStatusList() {
    try {
      const response = await this.makeRequest('getOrderStatusList.php');
      return this.handleResponse(response, 'Status list retrieved', 'Failed to retrieve order statuses');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getTaxList() {
    try {
      const response = await this.makeRequest('getTaxList.php');
      return this.handleResponse(response, 'Tax list retrieved', 'Failed to retrieve taxes');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getPaymentMethodList() {
    try {
      const response = await this.makeRequest('getPaymentMethodList.php');
      return this.handleResponse(response, 'Payment methods retrieved', 'Failed to retrieve payment methods');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — tax reverse (inclusive); optional order_date + service_start_date
  async createOrder({
    school_code,
    product_code,
    order_type = 'New',
    tax_id,
    promo_code_id = '',
    promo_code = '',
    promo_code_discount = 0,
    discount_percent = 0,
    billing_address_id = '',
    installment_count = 1,
    apply_demo_credit = true,
    order_date = '',
    service_start_date = '',
  }) {
    try {
      const params = this.createSearchParams({
        school_code,
        product_code,
        order_type,
        tax_id,
        promo_code_id,
        promo_code,
        promo_code_discount,
        discount_percent,
        billing_address_id,
        installment_count,
        apply_demo_credit: apply_demo_credit ? '1' : '0',
        order_date,
        service_start_date,
      });
      const response = await this.makeRequest(`createOrder.php?${params.toString()}`);
      return this.handleResponse(response, 'Order created', 'Failed to create order');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — full pipeline: create → pay → Confirm → subscription → product account
  async createBillWithPayment({
    school_code,
    product_code,
    order_type = 'New',
    tax_id,
    promo_code_id = '',
    promo_code = '',
    promo_code_discount = 0,
    discount_percent = 0,
    billing_address_id = '',
    installment_count = 1,
    apply_demo_credit = true,
    order_date = '',
    service_start_date = '',
    record_payment = false,
    transaction_amount = '',
    payment_method = '',
    payment_date = '',
    payment_remark = '',
    payment_channel = 'Manual',
    gateway_transaction_id = '',
    installment_id = '',
    renewal_rate = '',
    billing_cycle_months = '',
    installments = null,
  }) {
    try {
      const params = this.createSearchParams({
        school_code,
        product_code,
        order_type,
        tax_id,
        promo_code_id,
        promo_code,
        promo_code_discount,
        discount_percent,
        billing_address_id,
        installment_count,
        apply_demo_credit: apply_demo_credit ? '1' : '0',
        order_date,
        service_start_date,
        record_payment: record_payment ? '1' : '0',
        transaction_amount,
        payment_method,
        payment_date,
        payment_remark,
        payment_channel,
        gateway_transaction_id,
        installment_id,
        renewal_rate,
        billing_cycle_months,
        installments: Array.isArray(installments)
          ? JSON.stringify(installments)
          : '',
      });
      const response = await this.makeRequest(`createBillWithPayment.php?${params.toString()}`, {
        timeout: 45000,
      });
      return this.handleResponse(response, 'Bill processed', 'Failed to process bill');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — payment_channel: Manual | Payment Gateway
  async recordOrderPayment({
    order_id,
    school_code,
    transaction_amount,
    payment_method,
    payment_date,
    payment_remark = '',
    installment_id = '',
    payment_channel = 'Manual',
    gateway_transaction_id = '',
  }) {
    try {
      const params = this.createSearchParams({
        order_id,
        school_code,
        transaction_amount,
        payment_method,
        payment_date,
        payment_remark,
        installment_id,
        payment_channel,
        gateway_transaction_id,
      });
      const response = await this.makeRequest(`recordOrderPayment.php?${params.toString()}`);
      return this.handleResponse(response, 'Payment recorded', 'Failed to record payment');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — self-heal ENUM + payment_channel columns
  async ensureBillingSchema() {
    try {
      const response = await this.makeRequest('ensureBillingSchema.php');
      return this.handleResponse(response, 'Billing schema checked', 'Failed to ensure billing schema');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getSchoolSubscription(schoolCode, productCode = '') {
    try {
      const params = this.createSearchParams({ school_code: schoolCode, product_code: productCode });
      const response = await this.makeRequest(`getSchoolSubscription.php?${params.toString()}`);
      return this.handleResponse(response, 'Subscription retrieved', 'Failed to retrieve subscription');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async applyBillingAdjustment({
    subscription_id,
    source = 'Manual',
    bonus_days = 0,
    remark = '',
    created_by = '',
    source_ref = '',
    amount_delta = 0,
  }) {
    try {
      const params = this.createSearchParams({
        subscription_id,
        source,
        bonus_days,
        remark,
        created_by,
        source_ref,
        amount_delta,
      });
      const response = await this.makeRequest(`applyBillingAdjustment.php?${params.toString()}`);
      return this.handleResponse(response, 'Adjustment applied', 'Failed to apply adjustment');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async splitOrderInstallments(orderId, installmentCount) {
    try {
      const params = this.createSearchParams({
        order_id: orderId,
        installment_count: installmentCount,
      });
      const response = await this.makeRequest(`splitOrderInstallments.php?${params.toString()}`);
      return this.handleResponse(response, 'Installments created', 'Failed to split installments');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async backfillSubscriptions() {
    try {
      const response = await this.makeRequest('backfillSubscriptions.php');
      return this.handleResponse(response, 'Backfill complete', 'Backfill failed');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getGracePeriodList() {
    try {
      const response = await this.makeRequest('getGracePeriodList.php');
      return this.handleResponse(response, 'Grace periods retrieved', 'Failed to retrieve grace periods');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async upsertGracePeriod({ grace_days, product_code = '', remark = '' }) {
    try {
      const params = this.createSearchParams({ grace_days, product_code, remark });
      const response = await this.makeRequest(`upsertGracePeriod.php?${params.toString()}`);
      return this.handleResponse(response, 'Grace period saved', 'Failed to save grace period');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Dashboards ---

  async getAdminDashboardStats() {
    try {
      const response = await this.makeRequest('getCrmAdminDashboardStats.php');
      return this.handleResponse(response, 'Dashboard stats retrieved', 'Failed to retrieve dashboard stats');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getPlatformTeachingStats() {
    try {
      const response = await this.makeRequest('getPlatformTeachingStats.php');
      return this.handleResponse(response, 'Teaching stats retrieved', 'Failed to retrieve teaching stats');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // --- Telecaller / Promoter / Referral (Section C) ---

  async getTelecallerDashboard() {
    try {
      const response = await this.makeRequest('getTelecallerDashboard.php');
      return this.handleResponse(response, 'Telecaller data retrieved', 'Failed to retrieve telecaller data');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createTelecaller({ name, email = '', phone, daily_target = 20, specialization = '' }) {
    try {
      const params = this.createSearchParams({ name, email, phone, daily_target, specialization });
      const response = await this.makeRequest(`createTelecaller.php?${params.toString()}`);
      return this.handleResponse(response, 'Telecaller created', 'Failed to create telecaller');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createTelecallerCall(payload) {
    try {
      const params = this.createSearchParams(payload);
      const response = await this.makeRequest(`createTelecallerCall.php?${params.toString()}`);
      return this.handleResponse(response, 'Call scheduled', 'Failed to schedule call');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateTelecallerCall(payload) {
    try {
      const params = this.createSearchParams(payload);
      const response = await this.makeRequest(`updateTelecallerCall.php?${params.toString()}`);
      return this.handleResponse(response, 'Call updated', 'Failed to update call');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getPromoterDashboard() {
    try {
      const response = await this.makeRequest('getPromoterDashboard.php');
      return this.handleResponse(response, 'Promoter data retrieved', 'Failed to retrieve promoter data');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createPromoter({ name, email = '', phone, location = '', tier = 'Bronze', join_date }) {
    try {
      const params = this.createSearchParams({
        name,
        email,
        phone,
        location,
        tier,
        join_date: join_date || new Date().toISOString().slice(0, 10),
      });
      const response = await this.makeRequest(`createPromoter.php?${params.toString()}`);
      return this.handleResponse(response, 'Promoter created', 'Failed to create promoter');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — session/month referral registration dashboard
  async getReferralDashboard({ financial_year_id = '', year_month = '' } = {}) {
    try {
      const params = this.createSearchParams({ financial_year_id, year_month });
      const q = params.toString();
      const response = await this.makeRequest(
        q ? `getReferralDashboard.php?${q}` : 'getReferralDashboard.php'
      );
      return this.handleResponse(response, 'Referral data retrieved', 'Failed to retrieve referral data');
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createReferral(payload) {
    try {
      const params = this.createSearchParams(payload);
      const response = await this.makeRequest(`createReferral.php?${params.toString()}`);
      return this.handleResponse(response, 'Referral created', 'Failed to create referral');
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — map / fix school referal_code
  async mapSchoolReferral({ school_code, referal_code, referral_code }) {
    try {
      const params = this.createSearchParams({
        school_code,
        referal_code: referal_code || referral_code || '',
        referral_code: referral_code || referal_code || '',
      });
      const response = await this.makeRequest(`mapSchoolReferral.php?${params.toString()}`);
      return this.handleResponse(response, 'Referral mapped', 'Failed to map referral');
    } catch (error) {
      return this.handleError(error);
    }
  }

  // School Account Management API Methods
  async createSchoolAccount(accountData) {
    try {
      const response = await this.makeRequest('school-accounts/create.php', {
        method: 'POST',
        body: JSON.stringify(accountData),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Account created successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to create account',
          message: response.api_remark || 'Failed to create account',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  async getSchoolAccounts(schoolId = null) {
    try {
      const params = new URLSearchParams();
      if (schoolId) params.append('school_id', schoolId);

      const response = await this.makeRequest(`school-accounts/list.php?${params.toString()}`);

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response.accounts || [],
          message: 'Accounts retrieved successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to retrieve accounts',
          message: response.api_remark || 'Failed to retrieve accounts',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  async updateSchoolAccountTheme(accountId, themeName) {
    try {
      const response = await this.makeRequest(`school-accounts/update-theme.php?id=${accountId}`, {
        method: 'POST',
        body: JSON.stringify({ theme: themeName }),
      });

      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response,
          message: 'Theme updated successfully',
        };
      } else {
        return {
          success: false,
          error: response.api_remark || 'Failed to update theme',
          message: response.api_remark || 'Failed to update theme',
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Network error or server unavailable',
      };
    }
  }

  // Created By Cursor
  async getWhatsAppAccounts({ search = '', status = 'all', limit = 100, offset = 0 } = {}) {
    try {
      const params = this.createSearchParams({ search, status, limit, offset });
      const response = await this.makeRequest(`getWhatsAppAccounts.php?${params.toString()}`);
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || [],
          total: response.total || 0,
          message: 'WhatsApp accounts retrieved successfully',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve WhatsApp accounts',
        message: response.api_remark || 'Failed to retrieve WhatsApp accounts',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async createWhatsAppAccount(accountData) {
    try {
      const response = await this.makeRequest('createWhatsAppAccount.php', {
        method: 'POST',
        body: JSON.stringify(accountData),
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response,
          message: response.api_remark || 'WhatsApp account created successfully',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to create WhatsApp account',
        message: response.api_remark || 'Failed to create WhatsApp account',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppPricePlans({ activeOnly = false } = {}) {
    try {
      // Always pass an explicit flag so inactive + subscription plans are included for CRM admin.
      const params = this.createSearchParams(
        activeOnly ? { active_only: '1' } : { all: '1' }
      );
      const qs = params.toString();
      const response = await this.makeRequest(
        qs ? `getWhatsAppPricePlans.php?${qs}` : 'getWhatsAppPricePlans.php?all=1'
      );
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'Price plans retrieved successfully',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve price plans',
        message: response.api_remark || 'Failed to retrieve price plans',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async createWhatsAppPricePlan(payload) {
    try {
      const response = await this.makeRequest('createWhatsAppPricePlan.php', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response,
          message: response.api_remark || 'Price plan created',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to create price plan',
        message: response.api_remark || 'Failed to create price plan',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async updateWhatsAppPricePlan(payload) {
    try {
      const response = await this.makeRequest('updateWhatsAppPricePlan.php', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response,
          message: response.api_remark || 'Price plan updated',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to update price plan',
        message: response.api_remark || 'Failed to update price plan',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppSubscriptionPlans({ activeOnly = true } = {}) {
    try {
      const params = this.createSearchParams(activeOnly ? {} : { all: '1' });
      const qs = params.toString();
      const response = await this.makeRequest(
        qs ? `getWhatsAppSubscriptionPlans.php?${qs}` : 'getWhatsAppSubscriptionPlans.php'
      );
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'Subscription plans retrieved successfully',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to retrieve subscription plans',
        message: response.api_remark || 'Failed to retrieve subscription plans',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminOverview() {
    try {
      const response = await this.makeRequest('getWhatsAppAdminOverview.php', {
        timeout: 60000,
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'WhatsApp admin overview loaded',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load overview',
        message: response.api_remark || 'Failed to load overview',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminSchools({
    search = '',
    status = 'all',
    quality = 'all',
    plan = '',
    page = 1,
    limit = 25,
  } = {}) {
    try {
      const params = this.createSearchParams({
        search,
        status,
        quality,
        plan: plan || undefined,
        page,
        limit,
      });
      const response = await this.makeRequest(`getWhatsAppAdminSchools.php?${params.toString()}`);
      if (response.api_status === 'OK') {
        const data = response.data || {};
        return {
          success: true,
          data: Array.isArray(data.items) ? data.items : [],
          total: data.total || 0,
          page: data.page || page,
          limit: data.limit || limit,
          message: 'Schools retrieved',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load schools',
        message: response.api_remark || 'Failed to load schools',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminSchoolDetail(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(`getWhatsAppAdminSchoolDetail.php?${params.toString()}`);
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'School detail loaded',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load school detail',
        message: response.api_remark || 'Failed to load school detail',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppDashboard({ recent_limit = 20 } = {}) {
    try {
      const params = this.createSearchParams({ recent_limit });
      const response = await this.makeRequest(`getWhatsAppDashboard.php?${params.toString()}`, {
        timeout: 60000,
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'WhatsApp dashboard loaded',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load WhatsApp dashboard',
        message: response.api_remark || 'Failed to load WhatsApp dashboard',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppEmbeddedSignupDashboard({ recent_limit = 20 } = {}) {
    try {
      const params = this.createSearchParams({ recent_limit });
      const response = await this.makeRequest(
        `getWhatsAppEmbeddedSignupDashboard.php?${params.toString()}`
      );
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: 'WhatsApp dashboard loaded',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load WhatsApp dashboard',
        message: response.api_remark || 'Failed to load WhatsApp dashboard',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async updateWhatsAppServiceStatus({ school_code, status, remark = '' }) {
    try {
      const response = await this.makeRequest('updateWhatsAppServiceStatus.php', {
        method: 'POST',
        body: JSON.stringify({ school_code, status, remark }),
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || response,
          message: response.api_remark || 'Service status updated',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to update service status',
        message: response.api_remark || 'Failed to update service status',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminTemplates({
    search = '',
    status = 'all',
    school_code = '',
    limit = 50,
    offset = 0,
  } = {}) {
    try {
      const params = new URLSearchParams({
        search,
        status,
        limit: String(limit),
        offset: String(offset),
      });
      if (school_code) params.set('school_code', school_code);
      const response = await this.makeRequest(`getWhatsAppAdminTemplates.php?${params.toString()}`);
      if (response.api_status === 'OK') {
        return { success: true, data: response.data || {}, message: 'Templates loaded' };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load templates',
        message: response.api_remark || 'Failed to load templates',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async manageWhatsAppSchoolTemplate(payload = {}) {
    try {
      const response = await this.makeRequest('manageWhatsAppSchoolTemplate.php', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (response.api_status === 'OK') {
        return {
          success: true,
          data: response.data || {},
          message: response.api_remark || 'Template action completed',
        };
      }
      return {
        success: false,
        error: response.api_remark || 'Template action failed',
        message: response.api_remark || 'Template action failed',
        data: response.data || null,
        meta: response.meta || null,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminReports({ from_date, to_date } = {}) {
    try {
      const params = new URLSearchParams();
      if (from_date) params.set('from_date', from_date);
      if (to_date) params.set('to_date', to_date);
      const qs = params.toString();
      const response = await this.makeRequest(
        qs ? `getWhatsAppAdminReports.php?${qs}` : 'getWhatsAppAdminReports.php'
      );
      if (response.api_status === 'OK') {
        return { success: true, data: response.data || {}, message: 'Reports loaded' };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load reports',
        message: response.api_remark || 'Failed to load reports',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor
  async getWhatsAppAdminAuditLog({ limit = 100 } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      const response = await this.makeRequest(`getWhatsAppAdminAuditLog.php?${params.toString()}`);
      if (response.api_status === 'OK') {
        return { success: true, data: response.data || {}, message: 'Audit log loaded' };
      }
      return {
        success: false,
        error: response.api_remark || 'Failed to load audit log',
        message: response.api_remark || 'Failed to load audit log',
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  normalizePaginatedPayload(data) {
    if (Array.isArray(data)) {
      return {
        items: data,
        has_more: false,
        limit: data.length,
        offset: 0,
        count: data.length,
      };
    }
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      has_more: !!data?.has_more,
      limit: Number(data?.limit) || 0,
      offset: Number(data?.offset) || 0,
      count: Number(data?.count) || 0,
    };
  }

  async fetchDuplicateStudentEnrollmentsBulk({ school_code = '', onBatch } = {}) {
    const result = await this.getDuplicateStudentEnrollments({ school_code });
    if (!result.success) {
      return result;
    }
    const items = Array.isArray(result.data) ? result.data : [];
    onBatch?.({
      batch: 1,
      loaded: items.length,
      items,
      has_more: false,
    });
    return {
      success: true,
      data: items,
      message: 'Bulk fetch completed',
    };
  }

  async fetchPaginatedBulk({ fetchPage, batchSize = 100, onBatch }) {
    let offset = 0;
    let batch = 0;
    let hasMore = true;
    const allItems = [];

    while (hasMore) {
      batch += 1;
      const result = await fetchPage({ limit: batchSize, offset });
      if (!result.success) {
        return {
          ...result,
          data: allItems,
          partial: allItems.length > 0,
        };
      }

      const page = this.normalizePaginatedPayload(result.data);
      allItems.push(...page.items);
      onBatch?.({
        batch,
        loaded: allItems.length,
        items: page.items,
        has_more: page.has_more,
      });

      hasMore = page.has_more;
      offset += batchSize;
    }

    return {
      success: true,
      data: allItems,
      message: 'Bulk fetch completed',
    };
  }

  async getDuplicateStudentEnrollmentDetails({ school_code = '', student_enrollment_no = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code, student_enrollment_no });
      const response = await this.makeRequest(`getDuplicateStudentEnrollmentDetails.php?${params.toString()}`, {
        timeout: 30000,
      });
      return this.handleResponse(
        response,
        'Duplicate enrollment details retrieved',
        'Failed to retrieve duplicate enrollment details'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateStudentEnrollments({ school_code = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code });
      const response = await this.makeRequest(`getDuplicateStudentEnrollments.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        'Duplicate student enrollments retrieved',
        'Failed to retrieve duplicate student enrollments'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async fixDuplicateStudentEnrollments({ school_code = '', student_enrollment_no = '', dry_run = true } = {}) {
    try {
      const params = this.createSearchParams({
        school_code,
        student_enrollment_no,
        dry_run: dry_run ? '1' : '0',
      });
      const response = await this.makeRequest(`fixDuplicateStudentEnrollments.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        dry_run ? 'Duplicate enrollment dry run completed' : 'Duplicate enrollments fixed',
        'Failed to fix duplicate student enrollments'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateTransportDetails({ school_code = '', student_id = '', session_id = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code, student_id, session_id });
      const response = await this.makeRequest(`getDuplicateTransportDetails.php?${params.toString()}`, {
        timeout: 30000,
      });
      return this.handleResponse(
        response,
        'Duplicate transport details retrieved',
        'Failed to retrieve duplicate transport details'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateTransport({ school_code = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code });
      const response = await this.makeRequest(`getDuplicateTransport.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        'Duplicate transport assignments retrieved',
        'Failed to retrieve duplicate transport assignments'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async fixDuplicateTransport({ school_code = '', student_id = '', session_id = '', dry_run = true } = {}) {
    try {
      const params = this.createSearchParams({
        school_code,
        student_id,
        session_id,
        dry_run: dry_run ? '1' : '0',
      });
      const response = await this.makeRequest(`fixDuplicateTransport.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        dry_run ? 'Duplicate transport dry run completed' : 'Duplicate transport records fixed',
        'Failed to fix duplicate transport records'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateFeeTransactions({ school_code = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code });
      const response = await this.makeRequest(`getDuplicateFeeTransactions.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        'Duplicate fee payments retrieved',
        'Failed to retrieve duplicate fee payments'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateFeeTransactionDetails({
    school_code = '',
    session_id = '',
    student_id = '',
    class_id = '',
    section_id = '',
    fee_id = '',
    year_month = '',
    fee_payable = '',
    fee_paid = '',
  } = {}) {
    try {
      const params = new URLSearchParams({
        school_code: String(school_code ?? ''),
        session_id: String(session_id ?? ''),
        student_id: String(student_id ?? ''),
        class_id: String(class_id ?? ''),
        section_id: String(section_id ?? ''),
        fee_id: String(fee_id ?? ''),
        year_month: String(year_month ?? ''),
        fee_payable: String(fee_payable ?? ''),
        fee_paid: String(fee_paid ?? ''),
      });
      const response = await this.makeRequest(`getDuplicateFeeTransactionDetails.php?${params.toString()}`, {
        timeout: 30000,
      });
      return this.handleResponse(
        response,
        'Duplicate fee payment details retrieved',
        'Failed to retrieve duplicate fee payment details'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async fixDuplicateFeeTransactions({
    school_code = '',
    session_id = '',
    student_id = '',
    class_id = '',
    section_id = '',
    fee_id = '',
    year_month = '',
    fee_payable = '',
    fee_paid = '',
    dry_run = true,
    force = false,
  } = {}) {
    try {
      // Always send keys (including empty class/section) so targeted fix never drops filters.
      const params = new URLSearchParams({
        school_code: String(school_code ?? ''),
        session_id: String(session_id ?? ''),
        student_id: String(student_id ?? ''),
        class_id: String(class_id ?? ''),
        section_id: String(section_id ?? ''),
        fee_id: String(fee_id ?? ''),
        year_month: String(year_month ?? ''),
        fee_payable: String(fee_payable ?? ''),
        fee_paid: String(fee_paid ?? ''),
        dry_run: dry_run ? '1' : '0',
        force: force ? '1' : '0',
      });
      const response = await this.makeRequest(`fixDuplicateFeeTransactions.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        dry_run ? 'Duplicate fee payment dry run completed' : 'Duplicate fee payments fixed',
        'Failed to fix duplicate fee payments'
      );
    } catch (error) {
      const err = this.handleError(error);
      return {
        ...err,
        message:
          err.message === 'Network error or server unavailable' && error?.message
            ? `Fee fix request failed: ${error.message}`
            : err.message,
      };
    }
  }

  async getDuplicateMasterTransactions({ school_code = '' } = {}) {
    try {
      const params = this.createSearchParams({ school_code });
      const response = await this.makeRequest(`getDuplicateMasterTransactions.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        'Duplicate transactions retrieved',
        'Failed to retrieve duplicate transactions'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getDuplicateMasterTransactionDetails({
    school_code = '',
    session_id = '',
    student_id = '',
    class_id = '',
    section_id = '',
    paid_amount = '',
    transaction_date = '',
    transaction_for = 'FEE',
  } = {}) {
    try {
      const params = new URLSearchParams({
        school_code: String(school_code ?? ''),
        session_id: String(session_id ?? ''),
        student_id: String(student_id ?? ''),
        class_id: String(class_id ?? ''),
        section_id: String(section_id ?? ''),
        paid_amount: String(paid_amount ?? ''),
        transaction_date: String(transaction_date ?? ''),
        transaction_for: String(transaction_for || 'FEE'),
      });
      const response = await this.makeRequest(
        `getDuplicateMasterTransactionDetails.php?${params.toString()}`,
        { timeout: 30000 }
      );
      return this.handleResponse(
        response,
        'Duplicate transaction details retrieved',
        'Failed to retrieve duplicate transaction details'
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async fixDuplicateMasterTransactions({
    school_code = '',
    session_id = '',
    student_id = '',
    class_id = '',
    section_id = '',
    paid_amount = '',
    transaction_date = '',
    transaction_for = 'FEE',
    dry_run = true,
    force = false,
  } = {}) {
    try {
      const params = new URLSearchParams({
        school_code: String(school_code ?? ''),
        session_id: String(session_id ?? ''),
        student_id: String(student_id ?? ''),
        class_id: String(class_id ?? ''),
        section_id: String(section_id ?? ''),
        paid_amount: String(paid_amount ?? ''),
        transaction_date: String(transaction_date ?? ''),
        transaction_for: String(transaction_for || 'FEE'),
        dry_run: dry_run ? '1' : '0',
        force: force ? '1' : '0',
      });
      const response = await this.makeRequest(`fixDuplicateMasterTransactions.php?${params.toString()}`, {
        timeout: 120000,
      });
      return this.handleResponse(
        response,
        dry_run ? 'Duplicate transaction dry run completed' : 'Duplicate transactions fixed',
        'Failed to fix duplicate transactions'
      );
    } catch (error) {
      const err = this.handleError(error);
      return {
        ...err,
        message:
          err.message === 'Network error or server unavailable' && error?.message
            ? `Transaction fix request failed: ${error.message}`
            : err.message,
      };
    }
  }

  async getSchoolQueries(limit = 30, offset = 0) {
    try {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      const response = await this.makeRequest(`getAllQuery.php?${params.toString()}`, {
        timeout: 20000,
      });
      return this.handleResponse(response, "Queries loaded", "Failed to load queries");
    } catch (error) {
      return this.handleError(error, "Failed to load school queries");
    }
  }

  async suggestQueryReply(queryId) {
    try {
      const response = await this.makeRequest("suggestQueryReply.php", {
        method: "POST",
        body: JSON.stringify({ query_id: queryId }),
        timeout: 45000,
      });
      if (response.api_status === "OK" && response.reply) {
        return { success: true, reply: response.reply };
      }
      return {
        success: false,
        error: response.api_remark || "Could not suggest a reply",
      };
    } catch (error) {
      return this.handleError(error, "Could not suggest a reply");
    }
  }

  async replyToSchoolQuery({
    sender_id,
    query,
    query_to,
    query_type,
    parent_query_id,
    school_code,
  }) {
    try {
      const response = await this.makeRequest("AddNewQuery.php", {
        method: "POST",
        body: JSON.stringify({
          sender_id,
          query,
          query_to,
          query_type: query_type || "Reply",
          parent_query_id,
          school_code,
        }),
        timeout: 20000,
      });
      return this.handleResponse(response, "Reply sent", "Could not send reply");
    } catch (error) {
      return this.handleError(error, "Could not send reply");
    }
  }

  async getSupportChatInbox(filter = "all", adminId = "", limit = 30, offset = 0) {
    try {
      const response = await this.makeRequest("getSupportChatInbox.php", {
        method: "POST",
        body: JSON.stringify({
          filter,
          admin_id: adminId,
          limit,
          offset,
        }),
        timeout: 20000,
      });
      return this.handleResponse(response, "Inbox loaded", "Failed to load support inbox");
    } catch (error) {
      return this.handleError(error, "Failed to load support inbox");
    }
  }

  async getSupportChatMessages(chatId) {
    try {
      const response = await this.makeRequest("getSupportChatMessages.php", {
        method: "POST",
        body: JSON.stringify({ chat_id: chatId }),
        timeout: 20000,
      });
      if (response.api_status === "OK") {
        return { success: true, chat: response.chat, messages: response.messages || [] };
      }
      return { success: false, error: response.api_remark || "Failed to load chat" };
    } catch (error) {
      return this.handleError(error, "Failed to load chat");
    }
  }

  async claimSupportChat(chatId, adminId, adminName) {
    try {
      const response = await this.makeRequest("claimSupportChat.php", {
        method: "POST",
        body: JSON.stringify({
          chat_id: chatId,
          admin_id: adminId,
          admin_name: adminName,
        }),
        timeout: 20000,
      });
      if (response.api_status === "OK") {
        return { success: true, chat: response.chat, messages: response.messages || [] };
      }
      return { success: false, error: response.api_remark || "Could not claim chat" };
    } catch (error) {
      return this.handleError(error, "Could not claim chat");
    }
  }

  async replySupportChat(chatId, adminId, adminName, message) {
    try {
      const response = await this.makeRequest("replySupportChat.php", {
        method: "POST",
        body: JSON.stringify({
          chat_id: chatId,
          admin_id: adminId,
          admin_name: adminName,
          message,
        }),
        timeout: 20000,
      });
      if (response.api_status === "OK") {
        return { success: true, chat: response.chat, messages: response.messages || [] };
      }
      return { success: false, error: response.api_remark || "Could not send reply" };
    } catch (error) {
      return this.handleError(error, "Could not send reply");
    }
  }

  async releaseSupportChat(chatId, adminId, adminName) {
    try {
      const response = await this.makeRequest("releaseSupportChat.php", {
        method: "POST",
        body: JSON.stringify({
          chat_id: chatId,
          admin_id: adminId,
          admin_name: adminName,
        }),
        timeout: 20000,
      });
      if (response.api_status === "OK") {
        return { success: true, chat: response.chat, messages: response.messages || [] };
      }
      return { success: false, error: response.api_remark || "Could not resume AI" };
    } catch (error) {
      return this.handleError(error, "Could not resume AI");
    }
  }

  async closeSupportChatCrm(chatId, adminId, adminName) {
    try {
      const response = await this.makeRequest("closeSupportChatCrm.php", {
        method: "POST",
        body: JSON.stringify({
          chat_id: chatId,
          admin_id: adminId,
          admin_name: adminName,
        }),
        timeout: 20000,
      });
      if (response.api_status === "OK") {
        return { success: true, chat: response.chat, messages: response.messages || [] };
      }
      return { success: false, error: response.api_remark || "Could not close chat" };
    } catch (error) {
      return this.handleError(error, "Could not close chat");
    }
  }

  async getSupportChatSettings() {
    try {
      const response = await this.makeRequest("getSupportChatSettings.php", {
        timeout: 15000,
      });
      if (response.api_status === "OK" && response.settings) {
        return { success: true, settings: response.settings };
      }
      return {
        success: false,
        error: response.api_remark || "Could not load chat settings",
      };
    } catch (error) {
      return this.handleError(error, "Could not load chat settings");
    }
  }

  async updateSupportChatSettings({ service_enabled, ai_enabled, admin_id }) {
    try {
      const response = await this.makeRequest("updateSupportChatSettings.php", {
        method: "POST",
        body: JSON.stringify({
          service_enabled: service_enabled ? 1 : 0,
          ai_enabled: ai_enabled ? 1 : 0,
          admin_id: admin_id || "",
        }),
        timeout: 15000,
      });
      if (response.api_status === "OK" && response.settings) {
        return { success: true, settings: response.settings };
      }
      return {
        success: false,
        error: response.api_remark || "Could not save chat settings",
      };
    } catch (error) {
      return this.handleError(error, "Could not save chat settings");
    }
  }

  // Created By Cursor — school sessions for Transaction Panel
  async getSchoolSessionList(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(
        `getSchoolSessionList.php?${params.toString()}`
      );
      return this.handleResponse(
        response,
        "Sessions loaded",
        "No sessions found"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor — class/board/medium for Transaction Panel (class-wise report)
  async getSchoolClassList(schoolCode, sessionId) {
    try {
      const params = this.createSearchParams({
        school_code: schoolCode,
        session_id: sessionId,
      });
      const response = await this.makeRequest(
        `getSchoolClassList.php?${params.toString()}`
      );
      return this.handleResponse(
        response,
        "Classes loaded",
        "No classes found"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Created By Cursor — CRM order payments for Transaction Panel (tm_crm_order_payment)
  async getCrmOrderPayments({
    report_type = "today",
    school_code = "",
    date = "",
    start_date = "",
    end_date = "",
    year_month = "",
    limit = 2000,
  } = {}) {
    try {
      const params = this.createSearchParams({
        report_type,
        school_code,
        date,
        start_date,
        end_date,
        year_month,
        limit,
      });
      const response = await this.makeRequest(
        `getCrmOrderPayments.php?${params.toString()}`
      );
      if (response.api_status === "OK") {
        return {
          success: true,
          data: Array.isArray(response.data) ? response.data : [],
          count: response.count || 0,
          total_amount: Number(response.total_amount) || 0,
          message: "Order payments loaded",
        };
      }
      return {
        success: false,
        data: [],
        error: response.api_remark || "No payments found",
        message: response.api_remark || "No payments found",
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — SOP school health list
  async getSopSchoolList({
    health_band = "ALL",
    sop_stage = "ALL",
    q = "",
    limit = 200,
    offset = 0,
    auto_refresh = 1,
  } = {}) {
    try {
      const params = this.createSearchParams({
        health_band,
        sop_stage,
        q,
        limit,
        offset,
        auto_refresh,
      });
      const response = await this.makeRequest(
        `getSopSchoolList.php?${params.toString()}`,
        { timeout: auto_refresh ? 60000 : 20000 }
      );
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === "number" ? response.total : rows.length,
          summary: response.summary || null,
          message: "SOP schools retrieved",
        };
      }
      return this.handleResponse(
        response,
        "SOP schools retrieved",
        "Failed to retrieve SOP schools"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async getSopSchoolDetail(schoolCode) {
    try {
      const params = this.createSearchParams({ school_code: schoolCode });
      const response = await this.makeRequest(
        `getSopSchoolDetail.php?${params.toString()}`,
        { timeout: 45000 }
      );
      return this.handleResponse(
        response,
        "SOP school detail retrieved",
        "Failed to retrieve SOP school detail"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — Module Usage: module % stats only
  async getSopModuleUsageStats({ range = "last_30_days", modules = "" } = {}) {
    try {
      const params = this.createSearchParams({
        range,
        modules: Array.isArray(modules) ? modules.join(",") : modules,
      });
      const response = await this.makeRequest(
        `getSopModuleUsageStats.php?${params.toString()}`,
        { timeout: 90000 }
      );
      if (response?.api_status === "OK") {
        return {
          success: true,
          module_stats: Array.isArray(response.module_stats)
            ? response.module_stats
            : [],
          modules: Array.isArray(response.modules) ? response.modules : [],
          range: response.range || range,
          range_label: response.range_label || "",
          date_from: response.date_from || "",
          date_to: response.date_to || "",
          summary: response.summary || null,
          message: "SOP module usage stats retrieved",
        };
      }
      return this.handleResponse(
        response,
        "SOP module usage stats retrieved",
        "Failed to retrieve module usage stats"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — Module Usage: school list for selected modules
  async getSopModuleUsage({
    q = "",
    range = "last_30_days",
    modules = "",
    status = "all",
    sort = "score_desc",
    limit = 100,
    offset = 0,
  } = {}) {
    try {
      const params = this.createSearchParams({
        q,
        range,
        modules: Array.isArray(modules) ? modules.join(",") : modules,
        status,
        sort,
        limit,
        offset,
      });
      const response = await this.makeRequest(
        `getSopModuleUsage.php?${params.toString()}`,
        { timeout: 60000 }
      );
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === "number" ? response.total : rows.length,
          limit: typeof response.limit === "number" ? response.limit : limit,
          offset: typeof response.offset === "number" ? response.offset : offset,
          modules: Array.isArray(response.modules) ? response.modules : [],
          status: response.status || status,
          sort: response.sort || sort,
          range: response.range || range,
          range_label: response.range_label || "",
          date_from: response.date_from || "",
          date_to: response.date_to || "",
          summary: response.summary || null,
          message: "SOP module usage schools retrieved",
        };
      }
      return this.handleResponse(
        response,
        "SOP module usage schools retrieved",
        "Failed to retrieve module usage schools"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — Explore Activity page (KPI + using / not-using)
  async getSopExploreActivity({
    q = "",
    range = "today",
    activity = "login",
    tab = "using",
    sort = "records_desc",
    limit = 100,
    offset = 0,
  } = {}) {
    try {
      const params = this.createSearchParams({
        q,
        range,
        activity,
        tab,
        sort,
        limit,
        offset,
      });
      const response = await this.makeRequest(
        `getSopExploreActivity.php?${params.toString()}`,
        { timeout: 90000 }
      );
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === "number" ? response.total : rows.length,
          limit: typeof response.limit === "number" ? response.limit : limit,
          offset: typeof response.offset === "number" ? response.offset : offset,
          tab: response.tab || tab,
          activity: response.activity || activity,
          range: response.range || range,
          range_label: response.range_label || "",
          date_from: response.date_from || "",
          date_to: response.date_to || "",
          kpi: response.kpi || {},
          summary: response.summary || null,
          message: "SOP explore activity retrieved",
        };
      }
      return this.handleResponse(
        response,
        "SOP explore activity retrieved",
        "Failed to retrieve explore activity"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — SOP daily activity (range + activity filter + pagination)
  async getSopDailyActivity({
    q = "",
    date = "",
    range = "today",
    activity = "all",
    limit = 100,
    offset = 0,
  } = {}) {
    try {
      const params = this.createSearchParams({
        q,
        date,
        range,
        activity,
        limit,
        offset,
        only_active: 1,
      });
      const response = await this.makeRequest(
        `getSopDailyActivity.php?${params.toString()}`,
        { timeout: 60000 }
      );
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === "number" ? response.total : rows.length,
          limit: typeof response.limit === "number" ? response.limit : limit,
          offset: typeof response.offset === "number" ? response.offset : offset,
          date: response.date || "",
          date_from: response.date_from || "",
          date_to: response.date_to || "",
          range: response.range || range,
          range_label: response.range_label || "",
          activity: response.activity || activity,
          summary: response.summary || null,
          message: "SOP daily activity retrieved",
        };
      }
      return this.handleResponse(
        response,
        "SOP daily activity retrieved",
        "Failed to retrieve daily activity"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async refreshSchoolSopHealth({ school_code = "", limit = 50, stale_hours = 24 } = {}) {
    try {
      const params = this.createSearchParams({
        school_code,
        limit,
        stale_hours,
      });
      const response = await this.makeRequest(
        `refreshSchoolSopHealth.php?${params.toString()}`,
        { timeout: 90000 }
      );
      return this.handleResponse(
        response,
        response?.api_remark || "SOP health refreshed",
        "Failed to refresh SOP health"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async updateSopSchool(payload = {}) {
    try {
      const response = await this.makeRequest("updateSopSchool.php", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return this.handleResponse(
        response,
        "SOP school updated",
        "Failed to update SOP school"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor — tm_crm_announcement list
  async getCrmAnnouncementList({
    status = "ALL",
    school_recipient = "ALL",
    q = "",
    limit = 200,
    offset = 0,
  } = {}) {
    try {
      const params = this.createSearchParams({
        status,
        school_recipient,
        q,
        limit,
        offset,
      });
      const response = await this.makeRequest(
        `getCrmAnnouncementList.php?${params.toString()}`
      );
      if (response?.api_status === "OK") {
        const rows = Array.isArray(response.data) ? response.data : [];
        return {
          success: true,
          data: rows,
          total: typeof response.total === "number" ? response.total : rows.length,
          message: "Announcements retrieved",
        };
      }
      return this.handleResponse(
        response,
        "Announcements retrieved",
        "Failed to retrieve announcements"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async upsertCrmAnnouncement(payload = {}) {
    try {
      const response = await this.makeRequest("upsertCrmAnnouncement.php", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return this.handleResponse(
        response,
        response?.api_remark || "Announcement saved",
        "Failed to save announcement"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  //Created By Cursor
  async deleteCrmAnnouncement({ id, hard = false } = {}) {
    try {
      const response = await this.makeRequest("deleteCrmAnnouncement.php", {
        method: "POST",
        body: JSON.stringify({ id, hard: hard ? 1 : 0 }),
      });
      return this.handleResponse(
        response,
        response?.api_remark || "Announcement removed",
        "Failed to remove announcement"
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
